import { useIsJobillan } from '@/hooks'
import { ActivitySummary } from '@/shared/activity-summary'
import { Button } from '@/ui'
import { logger, SubtleLoader } from '@/ui'
import { JobillanBadge } from '@/ui/core/jobillan-badge'
import { type ActivityLabel, usePersonRefreshSummary } from '@jobilla/nexus-sdk'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { parseISO } from 'date-fns'
import { MailIcon, PhoneIcon, RefreshCwIcon, XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export type CandidateQuickPeekCandidacy = {
	candidateId: string
	campaign: { id: string; title: string }
	lastActivity: { label: ActivityLabel; at: string | null; actorName: string | null } | null
}

export type CandidateQuickPeekData = {
	name: string | null
	joinedAt: string | null
	summary: string | null
	emails: Array<{ value: string }>
	phones: Array<{ value: string }>
	skills: string[]
	candidacies: CandidateQuickPeekCandidacy[]
	previousNames: string[]
}

export type CandidateQuickPeekProps = {
	open: boolean
	onClose: () => void
	data: CandidateQuickPeekData | null
	isLoading?: boolean
	organisationId: string
	personId: string | null
	isSummaryGenerating?: boolean
	onSummaryRequested?: () => void
	renderCandidacyLink?: (candidacy: CandidateQuickPeekCandidacy) => ReactNode
	onExitComplete?: () => void
}

export function CandidateQuickPeek({
	open,
	onClose,
	data,
	isLoading,
	organisationId,
	personId,
	isSummaryGenerating,
	onSummaryRequested,
	renderCandidacyLink,
	onExitComplete,
}: CandidateQuickPeekProps) {
	return (
		<AnimatePresence initial={false} onExitComplete={onExitComplete}>
			{open
				? (
					<motion.aside
						key="candidate-quick-peek"
						initial={{ width: 0, opacity: 0 }}
						animate={{ width: 396, opacity: 1 }}
						exit={{ width: 0, opacity: 0 }}
						transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
						className="shrink-0 overflow-hidden flex justify-end sticky top-[calc(var(--header-height)+1rem)] max-h-[calc(100vh-var(--header-height)-1rem)]"
					>
						<div className="w-[380px] flex flex-col bg-white border border-grey-200 rounded-md shadow-sm">
							<PeekHeader data={data} onClose={onClose} />
							<div className="overflow-y-auto flex-1">
								{isLoading
									? (
										<div className="p-8">
											<SubtleLoader delay={0} />
										</div>
									)
									: data
									? (
										<PeekBody
											data={data}
											organisationId={organisationId}
											personId={personId}
											isSummaryGenerating={isSummaryGenerating}
											onSummaryRequested={onSummaryRequested}
											renderCandidacyLink={renderCandidacyLink}
										/>
									)
									: null}
							</div>
						</div>
					</motion.aside>
				)
				: null}
		</AnimatePresence>
	)
}

function PeekHeader({
	data,
	onClose,
}: {
	data: CandidateQuickPeekData | null
	onClose: () => void
}) {
	const { t, i18n } = useTranslation('common')

	const joinedFormatter = useMemo(
		() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }),
		[i18n.language],
	)

	return (
		<div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-grey-100">
			<div className="min-w-0">
				<h2 className="font-medium text-grey-900 truncate">
					{data?.name ?? '—'}
				</h2>
				{data?.previousNames && data.previousNames.length > 0
					? (
						<p className="text-xs text-grey-500 mt-0.5 italic truncate">
							{t('candidate_quick_peek.also_known_as', {
								names: data.previousNames.join(', '),
							})}
						</p>
					)
					: null}
				{data?.joinedAt
					? (
						<p className="text-xs text-grey-500 mt-0.5">
							{t('candidate_quick_peek.joined', {
								date: joinedFormatter.format(parseISO(data.joinedAt)),
							})}
						</p>
					)
					: null}
			</div>
			<button
				type="button"
				onClick={onClose}
				className="shrink-0 p-1 text-grey-500 hover:text-grey-900 hover:bg-grey-100 rounded cursor-pointer"
				title={t('candidate_quick_peek.close')}
			>
				<XIcon className="size-4" />
				<span className="sr-only">{t('candidate_quick_peek.close')}</span>
			</button>
		</div>
	)
}

function PeekBody({
	data,
	organisationId,
	personId,
	isSummaryGenerating,
	onSummaryRequested,
	renderCandidacyLink,
}: {
	data: CandidateQuickPeekData
	organisationId: string
	personId: string | null
	isSummaryGenerating?: boolean
	onSummaryRequested?: () => void
	renderCandidacyLink?: (candidacy: CandidateQuickPeekCandidacy) => ReactNode
}) {
	const { t } = useTranslation('common')
	const isJobillan = useIsJobillan()
	const aiSummaryEnabled = useBooleanFlagValue('talent-pool-ai-summary', false)
	const refreshSummary = usePersonRefreshSummary({
		onSuccess: () => onSummaryRequested?.(),
		onError: (error) => {
			logger.error(error, { data: { organisation_id: organisationId, person_id: personId } })
			refreshSummary.reset()
		},
	})

	const handleRefresh = () => {
		if (!personId) { return }
		logger.track('Talent pool: Candidate summary requested', {
			data: { person_id: personId, action: data.summary ? 'refresh' : 'generate' },
		})
		refreshSummary.mutate({ organisationId, person: personId })
	}

	return (
		<div className="divide-y divide-grey-100">
			{aiSummaryEnabled && isJobillan
				? (
					<Section
						title={t('candidate_quick_peek.section.summary')}
						titleSuffix={<JobillanBadge />}
					>
						{isSummaryGenerating
							? (
								<p className="text-xs text-grey-400 flex items-center gap-1.5">
									<RefreshCwIcon className="size-3 animate-spin" />
									{t('candidate_quick_peek.summary_generating')}
								</p>
							)
							: data.summary
							? (
								<div className="space-y-2">
									<p className="text-sm text-grey-700 leading-relaxed">{data.summary}</p>
									<Button
										type="subtle"
										onClick={handleRefresh}
										disabled={refreshSummary.isPending}
										size="tiny"
										prefix="refresh"
									>
										{t('candidate_quick_peek.summary_refresh')}
									</Button>
								</div>
							)
							: (
								<div className="space-y-2">
									<p className="text-xs text-grey-400">{t('candidate_quick_peek.no_summary')}</p>
									<Button
										type="subtle"
										onClick={handleRefresh}
										disabled={refreshSummary.isPending}
										size="tiny"
										prefix="sparkles"
									>
										{refreshSummary.isPending
											? t('candidate_quick_peek.summary_generating')
											: t('candidate_quick_peek.summary_generate')}
									</Button>
								</div>
							)}
					</Section>
				)
				: null}
			<Section title={t('candidate_quick_peek.section.contact')}>
				{data.emails.length === 0 && data.phones.length === 0
					? <EmptyNote text={t('candidate_quick_peek.no_contact')} />
					: (
						<ul className="space-y-1.5">
							{data.emails.map((email) => (
								<li key={email.value} className="flex items-center gap-2 text-sm text-grey-700">
									<MailIcon className="size-3.5 text-grey-400 shrink-0" />
									<span className="truncate">{email.value}</span>
								</li>
							))}
							{data.phones.map((phone) => (
								<li key={phone.value} className="flex items-center gap-2 text-sm text-grey-700">
									<PhoneIcon className="size-3.5 text-grey-400 shrink-0" />
									<span className="truncate">{phone.value}</span>
								</li>
							))}
						</ul>
					)}
			</Section>

			{isJobillan
				? (
					<Section
						title={t('candidate_quick_peek.section.skills')}
						titleSuffix={<JobillanBadge />}
					>
						{data.skills.length === 0
							? <EmptyNote text={t('candidate_quick_peek.no_skills')} />
							: (
								<div className="flex flex-wrap gap-1.5">
									{data.skills.map((skill) => (
										<span
											key={skill}
											className="inline-flex items-center px-2 py-0.5 text-xs bg-grey-100 text-grey-700 rounded-full"
										>
											{skill}
										</span>
									))}
								</div>
							)}
					</Section>
				)
				: null}

			<Section title={t('candidate_quick_peek.section.campaigns')}>
				{data.candidacies.length === 0
					? <EmptyNote text={t('candidate_quick_peek.no_campaigns')} />
					: (
						<ul className="space-y-1.5">
							{data.candidacies.map((candidacy) => (
								<li key={candidacy.candidateId} className="text-sm">
									<div className="text-brand-600 hover:underline">
										{renderCandidacyLink
											? renderCandidacyLink(candidacy)
											: candidacy.campaign.title}
									</div>
									{candidacy.lastActivity
										? (
											<div className="mt-0.5">
												<ActivitySummary
													label={candidacy.lastActivity.label}
													at={candidacy.lastActivity.at}
													actorName={candidacy.lastActivity.actorName}
												/>
											</div>
										)
										: null}
								</li>
							))}
						</ul>
					)}
			</Section>
		</div>
	)
}

function Section(
	{ title, titleSuffix, children }: {
		title: string
		titleSuffix?: ReactNode
		children: ReactNode
	},
) {
	return (
		<section className="px-4 py-3">
			<h3 className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-grey-500 font-medium mb-2">
				{title}
				{titleSuffix}
			</h3>
			{children}
		</section>
	)
}

function EmptyNote({ text }: { text: string }) {
	return <p className="text-xs text-grey-400">{text}</p>
}
